<script setup lang="ts">
import { useDependency } from '@/core/ioc/UseDependency'
import { useLayout } from '@/layout/composables/layout'
import type { IAppMenuItem } from '@/models/Menu/IAppMenuItem'
import type IPermissionChecker from '@/service/permissions/IPermissionChecker'
import { SERVICES_TYPES } from '@/service/ServicesTypes'
import { onBeforeMount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const { layoutState, setActiveMenuItem, toggleMenu } = useLayout()

interface Props {
  item: IAppMenuItem
  index: number
  root?: boolean
  parentItemKey?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  root: true,
  parentItemKey: null
})

const isActiveMenu = ref(false)
const itemKey = ref<string | null>(null)

onBeforeMount(() => {
  itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index)

  const activeItem = layoutState.activeMenuItem
  isActiveMenu.value = activeItem === itemKey.value || (activeItem ? activeItem.startsWith(itemKey.value + '-') : false)
})

watch(
  () => layoutState.activeMenuItem,
  newVal => {
    isActiveMenu.value = newVal === itemKey.value || (newVal ? newVal.startsWith(itemKey.value + '-') : false)
  }
)

watch(
  () => route.path,
  () => {
    updateActiveMenuItem()
  }
)

function updateActiveMenuItem() {
  if (checkActiveRoute(props.item)) {
    itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index)
    setActiveMenuItem(itemKey.value)
    const activeItem = layoutState.activeMenuItem
    isActiveMenu.value = activeItem === itemKey.value || (activeItem ? activeItem.startsWith(itemKey.value + '-') : false)
  }
}

function itemClick(event: Event, item: IAppMenuItem) {
  if (item.disabled) {
    event.preventDefault()
    return
  }

  if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
    toggleMenu()
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item })
  }

  const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey.value) : itemKey.value

  setActiveMenuItem(foundItemKey)
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '')
}

function checkActiveRoute(item: IAppMenuItem): boolean {
  return normalizePath(route.path).toLowerCase() === normalizePath(item.to ?? '#').toLowerCase()
}

function isItemVisibleWithPermissions(item: IAppMenuItem): boolean {
  if (!item.items || item.items.length === 0) {
    return permissionChecker.canShow(item.servicePermissions)
  }

  return item.items.some(child => isItemVisibleWithPermissions(child))
}

const isToggled = () => !layoutState.staticMenuDesktopInactive || layoutState.staticMenuMobileActive

const permissionChecker = useDependency<IPermissionChecker>(SERVICES_TYPES.PermissionChecker)
</script>

<template>
  <li v-if="isItemVisibleWithPermissions(item)" :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">
      {{ item.label }}
    </div>
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      @click="itemClick($event, item)"
      :class="[item.class, { 'no-hover-bg': !isToggled() }]"
      :target="item.target"
      tabindex="0"
    >
      <i :class="[item.icon, { 'big-icon': !isToggled() }]" class="layout-menuitem-icon" v-tooltip.right="!isToggled() ? item.label : null"></i>
      <span v-if="isToggled()" class="layout-menuitem-text">{{ item.label }}</span>
      <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </a>
    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      @click="itemClick($event, item)"
      :class="[item.class, { 'no-hover-bg': !isToggled() }, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to"
    >
      <i :class="[item.icon, { 'big-icon': !isToggled() }]" class="layout-menuitem-icon" v-tooltip.right="!isToggled() ? item.label : null"></i>
      <span v-if="isToggled()" class="layout-menuitem-text">{{ item.label }}</span>
      <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </router-link>
    <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
      <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
        <app-menu-item v-for="(child, i) in item.items" :key="i" :index="i" :item="child" :parent-item-key="itemKey" :root="false"></app-menu-item>
      </ul>
    </Transition>
  </li>
</template>

<style lang="css" scoped>
ul a:hover {
  background-color: var(--surface-hover);
}

ul a.no-hover-bg:hover {
  background-color: transparent !important;
}

.big-icon {
  font-size: 1.5rem;
}
</style>
